import React from 'react';
import humps from 'humps';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import {
  StyledTableContainer,
  PaginationContainer,
  PaginationStats,
  PaginationControls,
} from './PaginatedTable.styles';
import { SorterResult } from 'antd/es/table/interface';
import Pagination from 'components/Pagination';
import RowsPerPageSelect from './RowsPerPageSelect';

interface PaginatedTableProps<T> extends Omit<TableProps<T>, 'pagination'> {
  total?: number;
  loading?: boolean;
  fetching?: boolean;
  setTableParams?: React.Dispatch<React.SetStateAction<any>>;
  tableParams?: any;
  rowsPerPageOptions?: number[];
}

const PaginatedTable = <T extends object>({
  dataSource,
  columns,
  total = 0,
  loading = false,
  fetching = false,
  setTableParams,
  tableParams,
  rowsPerPageOptions = [10, 25, 50, 100],
  ...rest
}: PaginatedTableProps<T>) => {
  const onPageChange = (selectedItem: { selected: number }) => {
    const current = selectedItem.selected + 1;
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current,
      },
    });
  };

  const onTableChange: TableProps<T>['onChange'] = (_, __, sorter: SorterResult<T>) => {
    let sort: string | undefined;
    if (sorter?.field) {
      const order = sorter.order === 'ascend' ? '' : '-';
      sort = `${order}${humps.decamelize(sorter?.column?.sortKey || sorter?.field)}`;
    }

    setTableParams({
      ...tableParams,
      sort,
    });
  };

  const onRowsPerPageChange = (pageSize: number) => {
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        pageSize,
        current: 1, // Reset to first page when changing page size
      },
    });
  };

  const currentPageSize = tableParams?.pagination?.pageSize || 10;

  return (
    <StyledTableContainer>
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={fetching}
        pagination={false}
        onChange={onTableChange}
        {...rest}
      />
      {total > 0 && (
        <PaginationContainer>
          <RowsPerPageSelect
            value={currentPageSize}
            options={rowsPerPageOptions}
            onChange={onRowsPerPageChange}
          />
          <PaginationControls>
            <PaginationStats>
              {dataSource?.length} out of {total}
            </PaginationStats>
            <Pagination
              activePage={tableParams?.pagination?.current || 1}
              count={Math.ceil(total / currentPageSize)}
              onPageChange={onPageChange}
              isLoading={loading}
            />
          </PaginationControls>
        </PaginationContainer>
      )}
    </StyledTableContainer>
  );
};

export default PaginatedTable;
